import { Component, inject, input } from '@angular/core';
import { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { indicate } from 'src/app/operators';
import { FileResponse } from 'src/app/shared/models/autogenerated-piping';
import { DownloadService } from 'src/app/core/services/download.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

/**
 * Reusable Excel export button for dictionary admin tabs.
 *
 * Accepts an `exportFn` that returns an `Observable<FileResponse>` pointing to
 * the appropriate BE export endpoint. Handles the loading state, triggers the
 * download via `DownloadService`, and shows a snackbar on error.
 *
 * Usage:
 * ```html
 * <app-export-excel-button
 *   [exportFn]="exportFn"
 *   label="Export to Excel">
 * </app-export-excel-button>
 * ```
 *
 * In the host component:
 * ```typescript
 * exportFn = () => this.dictionaryExportService.exportCommonNotes();
 * ```
 */
@Component({
  selector: 'app-export-excel-button',
  templateUrl: './export-excel-button.component.html',
  styleUrls: ['./export-excel-button.component.scss'],
  standalone: false
})
export class ExportExcelButtonComponent {
  /** The function to call when the export button is clicked. Must return an Observable<FileResponse>. */
  exportFn = input.required<() => Observable<FileResponse>>();

  /** Optional button label. Defaults to 'Export to Excel'. */
  label = input<string>('Export to Excel');

  /** True while the export API call is in-flight. */
  exporting$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly downloadService: DownloadService = inject(DownloadService);
  private readonly snackBarService: SnackBarService = inject(SnackBarService);

  /** Invokes the provided exportFn and triggers the file download. */
  export(): void {
    this.downloadService.downloadFile(
      this.exportFn()().pipe(indicate(this.exporting$)),
    );
  }
}
